import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import type { MarkupTierDraft } from "@/lib/rateCardPreview";

export function MarkupTierEditor({
    tiers,
    onChange,
}: {
    tiers: MarkupTierDraft[];
    onChange: (tiers: MarkupTierDraft[]) => void;
}) {
    return (
        <div className="space-y-2">
            <Label>Tiered material markup bands</Label>
            <p className="text-xs text-muted-foreground">
                Multiplier applied to part cost within each dollar range.
            </p>
            {tiers.map((tier, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                    <div>
                        {idx === 0 && <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Min $</span>}
                        <Input
                            type="number"
                            placeholder="0"
                            value={tier.min_amount}
                            onChange={(e) => {
                                const next = [...tiers];
                                next[idx] = { ...tier, min_amount: e.target.value };
                                onChange(next);
                            }}
                        />
                    </div>
                    <div>
                        {idx === 0 && (
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Max $</span>
                        )}
                        <Input
                            type="number"
                            placeholder="Open"
                            value={tier.max_amount ?? ""}
                            onChange={(e) => {
                                const next = [...tiers];
                                next[idx] = { ...tier, max_amount: e.target.value || null };
                                onChange(next);
                            }}
                        />
                    </div>
                    <div>
                        {idx === 0 && (
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">× Multiplier</span>
                        )}
                        <Input
                            type="number"
                            step="0.001"
                            placeholder="1.6"
                            value={tier.multiplier}
                            onChange={(e) => {
                                const next = [...tiers];
                                next[idx] = { ...tier, multiplier: e.target.value };
                                onChange(next);
                            }}
                        />
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => onChange(tiers.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                    if (tiers.length > 0) {
                        const last = tiers[tiers.length - 1]!;
                        const lastMax = last.max_amount;
                        if (lastMax == null || lastMax === "") return;
                        const nextMin = Number(lastMax) + 0.01;
                        onChange([
                            ...tiers,
                            {
                                min_amount: String(nextMin),
                                max_amount: null,
                                multiplier: "1.4",
                                sort_order: tiers.length,
                            },
                        ]);
                        return;
                    }
                    onChange([
                        {
                            min_amount: "0",
                            max_amount: "1000",
                            multiplier: "1.6",
                            sort_order: 0,
                        },
                    ]);
                }}
            >
                <Plus className="h-4 w-4 mr-1" /> Add tier
            </Button>
        </div>
    );
}
